<template>
  <div v-if="currentUser.type=='Admin' || currentUser.type=='Seller'" class="api-page">
    <div class="api-container" id="genarateToken">
      <!-- Header -->
      <div class="api-header">
        <div>
          <h1 class="api-header-title">API Documentation</h1>
          <p class="api-header-desc">Integrate your systems with Shipsen using our REST API</p>
        </div>
        <button @click="generatePdf" class="api-print-btn" title="Print documentation">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2"/>
            <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4"/>
            <rect x="7" y="13" width="10" height="8" rx="2"/>
          </svg>
          <span>Print</span>
        </button>
      </div>

      <!-- Section: Token Generation -->
      <div class="api-card">
        <div class="api-card-label">
          <span class="api-method api-method-post">POST</span>
          <span class="api-card-label-text">Authentication</span>
        </div>
        <h2 class="api-card-title">Token Generation</h2>
        <p class="api-card-desc">Get an authorization token to authenticate your API requests. Tokens expire every <strong>5 minutes</strong>.</p>

        <div class="api-endpoint">
          <span class="api-endpoint-label">Endpoint</span>
          <code class="api-endpoint-url">https://api.shipsen.com/users/apilogin</code>
        </div>

        <div class="api-code-block">
          <div class="api-code-header">Body Params</div>
          <pre class="api-pre">{
  key: "<span class="api-redacted">your_api_key</span>",
  secret: "<span class="api-redacted">your_api_secret</span>"
}</pre>
        </div>

        <div class="api-code-block">
          <div class="api-code-header">Response <span class="api-status">200 OK</span></div>
          <pre class="api-pre">{
  token: "<span class="api-redacted">xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx</span>"
}</pre>
        </div>

        <div class="api-code-block">
          <div class="api-code-header">Required Headers</div>
          <pre class="api-pre"><strong>Content-Type</strong>: application/json;charset=utf-8
<strong>x-auth-token</strong>: {token}</pre>
        </div>

        <div class="api-note api-note-warning">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>Token expires every 5 minutes. Request a new one before expiry.</span>
        </div>
      </div>

      <!-- Section: Create Order -->
      <div class="api-card">
        <div class="api-card-label">
          <span class="api-method api-method-post">POST</span>
          <span class="api-card-label-text">Orders</span>
        </div>
        <h2 class="api-card-title">Create Order</h2>

        <div class="api-endpoint">
          <span class="api-endpoint-label">Endpoint</span>
          <code class="api-endpoint-url">https://api.shipsen.com/orders/apicreate</code>
        </div>

        <div class="api-code-block">
          <div class="api-code-header">Post Data</div>
          <pre class="api-pre">{
  orderId: { type: String, required: true, maxlength: 250 },     <span class="api-comment">// order id</span>
  source: { type: String, required: true, maxlength: 300 },      <span class="api-comment">// source de trafic</span>
  fullName: { type: String, required: true, maxlength: 250 },    <span class="api-comment">// customer name (*)</span>
  phone: { type: String, required: true, maxlength: 100 },       <span class="api-comment">// (*)</span>
  country: { type: String, required: true, maxlength: 2 },       <span class="api-comment">// country code</span>
  city: { type: String, maxlength: 50 },
  address: { type: String, required: true, maxlength: 250 },
  items: [                                                        <span class="api-comment">// products array</span>
    {
      name: { type: String, required: true, maxlength: 250 },    <span class="api-comment">// product name (*)</span>
      code: { type: String, required: true, maxlength: 100 },    <span class="api-comment">// product code (*)</span>
      quantity: { type: Number, required: true },                 <span class="api-comment">// (*)</span>
      price: { type: Number, required: true }                     <span class="api-comment">// unit price (*)</span>
    }
  ],
  total: { type: Number, required: true }                         <span class="api-comment">// total amount (*)</span>
}</pre>
        </div>

        <div class="api-code-block">
          <div class="api-code-header">Response <span class="api-status">200 OK</span></div>
          <pre class="api-pre">{
  id_order: "65855cc1f55c83e0f2bb8561"
}</pre>
        </div>
      </div>

      <!-- Section: Update Order -->
      <div class="api-card">
        <div class="api-card-label">
          <span class="api-method api-method-put">PUT</span>
          <span class="api-card-label-text">Orders</span>
        </div>
        <h2 class="api-card-title">Update Order</h2>

        <div class="api-endpoint">
          <span class="api-endpoint-label">Endpoint</span>
          <code class="api-endpoint-url">https://api.shipsen.com/orders/apiupdate?id={id_order}</code>
        </div>

        <div class="api-code-block">
          <div class="api-code-header">Post Data</div>
          <pre class="api-pre">{
  items: [                                                        <span class="api-comment">// products array</span>
    {
      name: { type: String, required: true, maxlength: 250 },    <span class="api-comment">// product name (*)</span>
      code: { type: String, required: true, maxlength: 100 },    <span class="api-comment">// product code (*)</span>
      quantity: { type: Number, required: true },                 <span class="api-comment">// (*)</span>
      price: { type: Number, required: true }                     <span class="api-comment">// unit price (*)</span>
    }
  ],
  total: { type: Number, required: true }                         <span class="api-comment">// total amount (*)</span>
}</pre>
        </div>

        <div class="api-code-block">
          <div class="api-code-header">Response <span class="api-status">200 OK</span></div>
          <pre class="api-pre">{
  id_order: "65855cc1f55c83e0f2bb8561"
}</pre>
        </div>
      </div>

      <!-- Section: Webhook -->
      <div class="api-card">
        <div class="api-card-label">
          <span class="api-method api-method-webhook">WEBHOOK</span>
          <span class="api-card-label-text">Integration</span>
        </div>
        <h2 class="api-card-title">Webhook Order Creation</h2>

        <div class="api-endpoint">
          <span class="api-endpoint-label">Webhook URL</span>
          <code class="api-endpoint-url">https://api.shipsen.com/orders/apicreate?key={KEY_CODE}&amp;secret={SECRET_CODE}&amp;source={SOURCE_NAME}</code>
        </div>
      </div>

      <!-- Section: Shopify Integration -->
      <div class="api-card">
        <div class="api-card-label">
          <span class="api-method api-method-webhook">GUIDE</span>
          <span class="api-card-label-text">Shopify</span>
        </div>
        <h2 class="api-card-title">Integration Shopify with Webhook</h2>

        <div class="api-steps">
          <div class="api-step">
            <span class="api-step-num">1</span>
            <span>Navigate to <strong>Settings</strong> &gt; <strong>Notification</strong> in your Shopify account.</span>
          </div>
          <div class="api-step">
            <span class="api-step-num">2</span>
            <span>Click <strong>Create webhook</strong>.</span>
          </div>
          <div class="api-step">
            <span class="api-step-num">3</span>
            <span>Specify the Event: choose <strong>Order Creation</strong>.</span>
          </div>
          <div class="api-step">
            <span class="api-step-num">4</span>
            <div>
              <span>Provide the Webhook URL:</span>
              <code class="api-inline-code">https://api.shipsen.com/orders/apicreate?key={KEY_CODE}&amp;secret={SECRET_CODE}&amp;source=SHOPIFY</code>
              <p class="api-step-note">Replace <strong>{KEY_CODE}</strong> and <strong>{SECRET_CODE}</strong> with your values from account settings.</p>
            </div>
          </div>
          <div class="api-step">
            <span class="api-step-num">5</span>
            <span>Specify Webhook API version: choose the <strong>latest</strong>.</span>
          </div>
          <div class="api-step">
            <span class="api-step-num">6</span>
            <span>Click <strong>Save</strong>.</span>
          </div>
        </div>

        <div class="api-note api-note-info">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <div>
            <p>Don't forget to sync products between Shopify and Shipsen.</p>
            <p>The Shopify product ID is in the product page URL (e.g., <code>https://admin.shopify.com/store/storeino/products/<strong>68527971688925</strong></code>).</p>
            <p>Enter this product ID in your Shipsen product's <strong>code</strong> field.</p>
          </div>
        </div>

        <div class="api-note api-note-default">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>Si vous utilisez le m&ecirc;me produit dans plusieurs pays, vous pouvez ajouter le <strong>country code</strong> dans la section <strong>Vendeur</strong> dans la partie <strong>edit produit</strong> (exemple: CI) dans votre compte Shopify.</span>
        </div>
      </div>

      <!-- Section: Google Sheets -->
      <div class="api-card">
        <div class="api-card-label">
          <span class="api-method api-method-get">GUIDE</span>
          <span class="api-card-label-text">Google Sheets</span>
        </div>
        <h2 class="api-card-title">Integration Google Sheets</h2>
        <p class="api-card-desc">Automatically send orders from Google Sheets to Shipsen.</p>

        <button :disabled="currentUser.type !== 'Seller'" @click="$vm2.open('ModalSettings')" class="api-generate-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="9" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="15" y2="14"/><line x1="9" y1="6" x2="11" y2="6"/></svg>
          <span>Generate Integration Code</span>
        </button>
      </div>

      <!-- Notes -->
      <div class="api-card">
        <h2 class="api-card-title" style="font-size: 16px;">General Notes</h2>
        <div class="api-note api-note-default" style="margin-top: 12px;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span><strong>Country</strong>: Use the country code (e.g., Maroc: MA, C&ocirc;te d'Ivoire: CI ...)</span>
        </div>
        <div class="api-note api-note-default">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>Le nom du produit doit &ecirc;tre le m&ecirc;me que le nom cr&eacute;&eacute; en Plateforme Shipsen.</span>
        </div>
      </div>
    </div>

    <!-- Modal: Google Sheets Integration -->
    <vue-modal-2 v-if="currentUser.type === 'Seller'" name="ModalSettings" :headerOptions="{title: 'Generate code d\'integration'}" :noFooter="true" modalSize="xl" @onclose="$vm2.close('ModalSettings')">
      <div class="bg-white rounded-lg">
        <div class="bg-white px-4 pt-0 pb-1 mb-20">
          <div class="Note tab-section">
            <p class="Note"><strong>Note</strong>: Votre fichier Excel doit respecter le m&ecirc;me format que dans cette exemple ci-dessous (<b>9 columns</b>)</p>
            <p class="Note"><strong>Note</strong>: Les champs qui sont obligatoire sont marqu&eacute; avec un cadre</p>
            <p class="Note"><strong>COUNTRY CODE</strong>: C'est le code de country tu trouve dans la section settings</p>
            <p class="Note"><strong>COUNTRY NAME</strong>: Specifie le meme country name que vous avez dans votre store (tu peu ajouter les country de chaque warehouse dans la section Prefix order dans la partie settings)</p>
            <table class="zigzag">
              <thead>
                <tr>
                  <th class="header">ORDER ID</th>
                  <th class="header">PRODUCT ID</th>
                  <th class="header">CONSTUMER NAME</th>
                  <th class="header">PHONE</th>
                  <th class="header">CITY/ADRESSE</th>
                  <th class="header">Total</th>
                  <th class="header">QUANTITY</th>
                  <th class="header">STORE NAME</th>
                  <th class="header">COUNTRY CODE/ COUNTRY NAME</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>XX_001</td>
                  <td class="imp">kv6qyr4w</td>
                  <td class="imp">Hassan Shipsen</td>
                  <td class="imp">06 xx xx xx xx</td>
                  <td>CITY</td>
                  <td class="imp">21600</td>
                  <td class="imp">1</td>
                  <td>Storeino</td>
                  <td class="imp">CI</td>
                </tr>
                <tr>
                  <td>XX_002</td>
                  <td class="imp">kt6qyr4w</td>
                  <td class="imp">Hassan Shipsen</td>
                  <td class="imp">07 xx xx xx xx</td>
                  <td>ADRESSE</td>
                  <td class="imp">14000</td>
                  <td class="imp">2</td>
                  <td>STORE X</td>
                  <td class="imp">C&ocirc;te d'Ivoire</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="tab-section mt-2">
            <div class="title-xx">
              <h3>Install Base Code</h3>
              <p>The code is a javascript snippet added to the Apps Script section of your Google Sheets.</p>
            </div>

            <div class="section-1">
              <div class="number"><span><strong>1</strong></span></div>
              <div class="title">Copy base code</div>
              <div class="sous-title">Copy the base code below.</div>
              <div class="section-code">
                <pre class="section-pre" id="codexcel">
/* Javascript Api Create Order shipsen Code */
/* Get data */
  function lastRowData(){
        var ss = SpreadsheetApp.getActive()
        var sheet = ss.getActiveSheet()
        var lastRow = sheet.getLastRow()
        var lastCol = sheet.getLastColumn()
        var lastRowData = sheet.getRange(lastRow,1,1,lastCol).getValues()
        return lastRowData[0]
  }

  /* Order creation API */
  function sendOrder(e) {
    if(e.changeType=="INSERT_ROW" || e.changeType=="EDIT"){
        var lastRow = lastRowData()
        var data = {
            'orderId':     lastRow[0],  /* Order id  */
            'fullName' :    lastRow[2], /* customer Name  */
            'phone' :     lastRow[3], /* customer phone  */
            'country' :         lastRow[8], /* country code  */
            'city' :         lastRow[4],
            'address' :         lastRow[4],
            'total' :         lastRow[5], /* Total price order  */
            'storeName':     lastRow[7],  /* Store Name  */
            'items' : [{
                          'name':  lastRow[1], /* Name product  */
                          'code':  lastRow[1], /* id or code product   */
                          'quantity':  lastRow[6],
                          'price':  lastRow[5] /* Unit price product  */
                      }]
        }

      var options = {
          'method': 'post',
          'contentType': 'application/json',
          'payload': JSON.stringify(data),
          'timeout' : '0',
          'muteHttpExceptions' : true,
      };

    var response = UrlFetchApp.fetch("https://api.shipsen.com/orders/apicreate?key={{currentUser.api.key}}&amp;secret={{currentUser.api.secret}}&amp;source=SHEET", options);

    }
  }
  /* End Api Create Order shipsen Code */
                </pre>
                <div class="section-bottom"></div>
              </div>
              <button class="btn" @click="copyIt('codexcel')">Copy Code</button>
            </div>

            <div class="section-1 mt-5">
              <div class="number"><span><strong>2</strong></span></div>
              <div class="title">Paste the code into Code.gs</div>
              <div class="sous-title">Select <b>Extensions</b> &gt; <b>Apps Script</b> &gt; <b>Code.gs</b> from within Google Sheets.<br> Press button <b>Save the project</b>.</div>
            </div>

            <div class="section-1 mt-5">
              <div class="number"><span><strong>3</strong></span></div>
              <div class="title">Navigate to <b>Trigger</b></div>
              <ol class="steps bx--list--ordered--temporary list--ordered--indented">
                <li class="step stepexpand bx--list__item"><span class="cmd">Click button <span class="ph uicontrol">Create Trigger</span>.</span></li>
                <li class="step stepexpand bx--list__item"><span class="cmd">Choose the function to perform: <span class="ph uicontrol">sendOrder</span>.</span></li>
                <li class="step stepexpand bx--list__item"><span class="cmd">Select the event source: <span class="ph uicontrol">Spreadsheet-based</span>.</span></li>
                <li class="step stepexpand bx--list__item"><span class="cmd">Select an event type: <span class="ph uicontrol">During a change</span>.</span></li>
                <li class="step stepexpand bx--list__item"><span class="cmd">Failure notification settings: <span class="ph uicontrol">Receive a notification every day</span>.</span></li>
                <li class="step stepexpand bx--list__item"><span class="cmd">Click <span class="ph uicontrol">Save</span>.</span></li>
              </ol>
            </div>
          </div>
        </div>

        <div class="modal-footer px-5 border0-t text-right">
          <a href="#" @click="$vm2.close('ModalSettings')" class="flex text-white mx-auto max-w-sm rounded text-center bg-gray-500 py-2 px-8 items-center focus:outline-none md:float-right">Close</a>
        </div>
        <div id="body-bottom"></div>
      </div>
    </vue-modal-2>
  </div>
</template>

<script>
export default {
  props: {
     currentUser: { type: Object},
     idWharhouse: { type: Object}
   },
  data() {
    return {
      formData: {
        token: null,
        seller:null,
      },
      sellers:[],
      showSelect:true,
      items: [
        { id: 0, title: 'Item A', list: 1 },
        { id: 1, title: 'Item B', list: 1 },
        { id: 2, title: 'Item C', list: 2 },
      ],
    };
  },
  computed: {
      warhouseSelected(){
        return this.$store.getters["wharhouse/warhouseSelected"]({user:_.pick(this.currentUser,['countries'])})
      },
      listOne() {
         return this.items.filter((item) => item.list === 1)
      },
      listTwo() {
        return this.items.filter((item) => item.list === 2)
      },
   },
   watch: {
      'warhouseSelected': async function(oldVal, newVal) {
      }
  },
  async mounted() {
  },
  methods: {
    copyIt: function(id) {
        const copyText = document.getElementById(id).textContent;
        const textArea = document.createElement('textarea');
        textArea.textContent = copyText;
        document.getElementById('body-bottom').append(textArea);
        textArea.select();
        document.execCommand("copy");
        document.getElementById("body-bottom").innerHTML = "";
    },
    startDrag(evt, item) {
      evt.dataTransfer.dropEffect = 'move'
      evt.dataTransfer.effectAllowed = 'move'
      evt.dataTransfer.setData('itemID', item.id)
    },
    onDrop(evt, list) {
      const itemID = evt.dataTransfer.getData('itemID')
      const item = this.items.find((item) => item.id == itemID)
      item.list = list
    },
    generatePdf(){
          var printContents = document.getElementById("genarateToken").innerHTML;
          var mywindow = window.open('', 'PRINT', 'height=400,width=600');
          mywindow.document.write('<html lang="en"><head><meta charset="utf-8"><title>API Documentation</title>');
          mywindow.document.write('<style type="text/css">body{font-family:system-ui,sans-serif;padding:20px;color:#1a1a2e;} .api-card{border:1px solid #e5e7eb;border-radius:12px;padding:24px;margin-bottom:16px;} .api-card-title{font-size:18px;font-weight:700;margin:0 0 8px;} .api-endpoint-url{background:#f0f4ff;padding:8px 14px;border-radius:8px;font-size:13px;display:block;margin:8px 0;word-break:break-all;} .api-pre{background:#1e293b;color:#e2e8f0;padding:16px;border-radius:8px;font-size:12px;white-space:pre;overflow-x:auto;} .api-comment{color:#6ee7b7;} .api-redacted{color:#f87171;} .api-note{padding:12px 16px;border-radius:8px;font-size:13px;margin:12px 0;} .api-note-warning{background:#fef3c7;color:#92400e;} .api-note-info{background:#eff6ff;color:#1e40af;} .api-note-default{background:#f3f4f6;color:#4b5563;} .api-method{padding:3px 10px;border-radius:6px;font-size:11px;font-weight:700;color:#fff;} .api-method-post{background:#4285f4;} .api-method-put{background:#f59e0b;} .api-method-webhook{background:#8b5cf6;} .api-method-get{background:#34a853;} .api-steps{display:flex;flex-direction:column;gap:12px;margin:16px 0;} .api-step{display:flex;align-items:flex-start;gap:12px;} .api-step-num{width:28px;height:28px;border-radius:50%;background:#4285f4;color:#fff;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0;} .api-status{background:#dcfce7;color:#166534;padding:2px 8px;border-radius:6px;font-size:11px;font-weight:600;margin-left:8px;} .api-print-btn,.api-generate-btn{display:none;}</style>');
          mywindow.document.write('</head><body>');
          mywindow.document.write(printContents);
          mywindow.document.write('</body></html>');
          mywindow.document.close();
          mywindow.focus();
          setTimeout(function () {
              mywindow.print();
              mywindow.close();
          }, 500);
          return false;
    },
    async getUser() {
      const res = await this.$me;
      this.currentUser = res.content;
    },
    async setSelected(seller) {
      if (seller) {
        this.formData.seller=seller;
        const res=await this.$server.generateToken("users", seller);
        this.formData.token=res.content;
      }
    },
    async getSellers() {
      const res = await this.$server.search("users", { type: "Seller",countries:await this.warhouseSelected,limit:10 });
      if (res.content.results) {
        this.sellers = res.content.results;
      }
    },
    ShowPopUpInformation(index,product) {
      this.selectProduct=product;
      let modal_id = "modal_" + index;
      this.$vm2.open(modal_id);
    },
  },
};
</script>

<style scoped>
/* ===== Page ===== */
.api-page {
  min-height: 100vh;
  background: #f0f2f5;
  padding: 72px 16px 40px;
}
.api-container {
  max-width: 900px;
  margin: 0 auto;
}

/* ===== Header ===== */
.api-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}
.api-header-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0;
  letter-spacing: -0.02em;
}
.api-header-desc {
  font-size: 14px;
  color: #6b7280;
  margin: 4px 0 0;
}
.api-print-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #4285f4;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.api-print-btn:hover {
  border-color: #4285f4;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.1);
}

/* ===== Card ===== */
.api-card {
  background: #fff;
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.03);
}
.api-card-label {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.api-card-label-text {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.api-card-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px;
}
.api-card-desc {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 20px;
  line-height: 1.6;
}

/* ===== Method Badge ===== */
.api-method {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.05em;
}
.api-method-post { background: #4285f4; }
.api-method-put { background: #f59e0b; }
.api-method-get { background: #34a853; }
.api-method-webhook { background: #8b5cf6; }

/* ===== Endpoint ===== */
.api-endpoint {
  margin: 16px 0;
}
.api-endpoint-label {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: block;
  margin-bottom: 6px;
}
.api-endpoint-url {
  display: block;
  background: #f0f4ff;
  color: #1e40af;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-family: 'Courier New', monospace;
  word-break: break-all;
  border: 1px solid #dbeafe;
}

/* ===== Code Block ===== */
.api-code-block {
  margin: 16px 0;
}
.api-code-header {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}
.api-status {
  background: #dcfce7;
  color: #166534;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
  text-transform: none;
  letter-spacing: 0;
}
.api-pre {
  background: #1e293b;
  color: #e2e8f0;
  padding: 20px;
  border-radius: 12px;
  font-size: 13px;
  font-family: 'Courier New', monospace;
  line-height: 1.7;
  overflow-x: auto;
  white-space: pre;
  margin: 0;
}
.api-comment {
  color: #6ee7b7;
}
.api-redacted {
  color: #f87171;
}

/* ===== Notes ===== */
.api-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.6;
  margin: 16px 0;
}
.api-note svg {
  flex-shrink: 0;
  margin-top: 2px;
}
.api-note p {
  margin: 0 0 4px;
}
.api-note p:last-child {
  margin: 0;
}
.api-note-warning {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}
.api-note-info {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}
.api-note-default {
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

/* ===== Steps ===== */
.api-steps {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 20px 0;
}
.api-step {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
}
.api-step-num {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4285f4, #5a9cf6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}
.api-step-note {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0;
}
.api-inline-code {
  display: block;
  background: #f0f4ff;
  color: #1e40af;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-family: 'Courier New', monospace;
  margin: 8px 0;
  word-break: break-all;
  border: 1px solid #dbeafe;
}

/* ===== Generate Button ===== */
.api-generate-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #4285f4, #5a9cf6);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
  margin-top: 8px;
}
.api-generate-btn:hover {
  box-shadow: 0 4px 14px rgba(66, 133, 244, 0.4);
  transform: translateY(-1px);
}
.api-generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* ===== Modal Styles (kept) ===== */
textarea { position: absolute; left: -100%; }
.section-1 { width: 100%; padding-left: 40px; position: relative; }
.section-1 .btn { vertical-align: middle; display: inline-flex; max-width: 100%; padding: 8px 16px; border-radius: 6px; border: none; cursor: pointer; background-color: #4285f4; color: white; font-weight: 600; }
.section-1 .btn:focus,.section-1 .btn:visited { background-color: #3367d6; }
.section-1 .number { height: 24px; width: 24px; border-radius: 50%; justify-content: center; display: flex; align-items: center; left: 0; top: 0; position: absolute; border: 2px solid #4285f4; color: #4285f4; }
.section-1 .title { min-width: 0; margin-bottom: 8px; font-weight: bold; color: rgba(0,0,0,.85); font-size: 1rem!important; }
.section-1 .sous-title { margin-bottom: 12px; color: rgba(0,0,0,.85); line-height: 1.42858!important; }
.section-code { margin-bottom: 20px; border-radius: 8px; overflow: hidden; position: relative; height: 135px; border: 1px solid #e5e7eb; width: 60%; }
.section-code pre { padding: 16px 20px; margin: 0; overflow: hidden; white-space: pre-line; background: #1e293b; color: #e2e8f0; font-size: 11px; line-height: 1.5; }
.section-bottom { width: 100%; bottom: 0; position: absolute; top: 16px; background-image: linear-gradient(rgba(30,41,59,.0),rgba(30,41,59,1.0)); }
.imp { border: 2px solid rgb(245, 133, 4); }
.Note table { font-family: sans-serif; display: block; width: 100%; overflow: scroll; }
.Note table th,.Note table td { padding: 0.25em 0.5em; text-align: left; }
.Note table td { background-color: #f9fafb; }
.Note table th { background-color: #1e293b; color: #fff; font-size: 13px; }
.tab-section { padding: 12px; border: 1.5px dashed #d1d5db; border-radius: 10px; }
.zigzag { border-collapse: separate; border-spacing: 0.2em 0.33em; }
p.Note { font-size: 12px; background-color: #f3f4f6; padding: 11px; display: block; margin-top: 4px; border-radius: 6px; }
h3 { color: rgba(0,0,0,.85); font-size: 1.125rem!important; font-weight: bold; }
.bx--list--ordered--temporary { font-size: 1rem !important; font-weight: 400 !important; list-style-type: decimal !important; line-height: 1.5rem !important; margin-left: 1.75rem !important; margin-bottom: 1.5rem !important; list-style: none; }
.bx--list--ordered--temporary > .bx--list__item { padding-left: .25rem !important; }
.bx--list__item { color: #161616; margin-top: .25rem; }
.uicontrol { font-weight: bold; }
</style>
